import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useEvent } from '@/context/EventContext';
import { useAuth } from '@/context/AuthContext';
import { CricketScore } from '@/components/schedule/CricketScore';
import {
  Home, Trophy, Calendar, Users, BarChart3, Clock,
  CheckCircle2, CircleDot, ArrowRight, Loader2, GitBranch,
  Target, Swords, Medal
} from 'lucide-react';

const FORMAT_LABELS = {
  round_robin: 'Round Robin',
  double_round_robin: 'Double Round Robin',
  single_elimination: 'Single Elimination',
  double_elimination: 'Double Elimination',
  consolation: '8 Team: 3 Game Guarantee',
  hybrid: 'Hybrid',
};

function ProgressRing({ percent, size = 80, strokeWidth = 6 }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#00247D"
        strokeWidth={strokeWidth} strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" className="transition-all duration-700" />
    </svg>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-[#00247D]', testId }) {
  return (
    <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color === 'text-green-600' ? 'bg-green-50' : color === 'text-amber-600' ? 'bg-amber-50' : color === 'text-red-600' ? 'bg-red-50' : 'bg-blue-50'}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
          <div className={`text-2xl font-bold ${color}`} style={{ fontFamily: 'Barlow Condensed' }}>{value}</div>
          <div className="text-xs text-slate-500">{label}</div>
          {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function MatchRow({ match, isCompleted }) {
  const winnerIsA = match.winner === match.team_a;
  const winnerIsB = match.winner === match.team_b;
  return (
    <div className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${isCompleted ? 'bg-slate-50 border-slate-100' : 'bg-white border-slate-200'}`}
      data-testid={`dashboard-match-${match.id}`}>
      <div className="flex-shrink-0">
        {isCompleted ? (
          <CheckCircle2 className="w-4 h-4 text-green-500" />
        ) : (
          <CircleDot className="w-4 h-4 text-amber-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${winnerIsA ? 'text-green-700 font-bold' : 'text-slate-700'}`}>
            {match.team_a_name || 'TBD'}
          </span>
          {isCompleted && (
            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              <CricketScore match={match} side="a" />
            </span>
          )}
          <span className="text-xs text-slate-400">vs</span>
          {isCompleted && (
            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              <CricketScore match={match} side="b" />
            </span>
          )}
          <span className={`text-sm font-medium truncate ${winnerIsB ? 'text-green-700 font-bold' : 'text-slate-700'}`}>
            {match.team_b_name || 'TBD'}
          </span>
        </div>
      </div>
      <div className="flex-shrink-0 text-right">
        <Badge variant="outline" className="text-[10px]">
          {match.stage?.replace('PO_', '') || `R${match.round}`}
        </Badge>
      </div>
      {match.date && (
        <div className="flex-shrink-0 text-xs text-slate-400 whitespace-nowrap">
          {new Date(match.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          {match.time ? ` ${match.time}` : ''}
        </div>
      )}
    </div>
  );
}

function CompactStandings({ standings }) {
  if (!standings) return null;
  const rows = standings.standings || [];
  if (!rows.length) {
    const raw = standings;
    if (typeof raw === 'object' && !Array.isArray(raw) && !raw.standings) {
      const teamRows = Object.entries(raw).map(([id, s]) => ({
        team_id: id,
        team_name: s.team_name,
        rank: s.rank,
        wins: s.wins || 0,
        losses: s.losses || 0,
        points_for: s.points_for || 0,
        points_against: s.points_against || 0,
      })).sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999) || b.wins - a.wins || a.losses - b.losses);
      return <StandingsTable rows={teamRows} />;
    }
    return null;
  }
  return <StandingsTable rows={rows} />;
}

function StandingsTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="dashboard-standings-table">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">#</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Team</th>
            <th className="px-3 py-2 text-center text-xs font-semibold text-green-600">W</th>
            <th className="px-3 py-2 text-center text-xs font-semibold text-red-500">L</th>
            <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500">PF</th>
            <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500">PA</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((team, idx) => (
            <tr key={team.team_id || idx} className={`border-t border-slate-100 ${idx === 0 ? 'bg-amber-50/50' : ''}`}>
              <td className="px-3 py-2">
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${
                  idx === 0 ? 'bg-amber-400 text-white' : idx === 1 ? 'bg-slate-300 text-slate-700' : idx === 2 ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-500'
                }`}>{idx + 1}</span>
              </td>
              <td className="px-3 py-2 text-sm font-medium text-slate-800">{team.team_name}</td>
              <td className="px-3 py-2 text-center text-sm font-semibold text-green-600">{team.wins}</td>
              <td className="px-3 py-2 text-center text-sm text-red-500">{team.losses}</td>
              <td className="px-3 py-2 text-center text-xs text-slate-500">{team.points_for}</td>
              <td className="px-3 py-2 text-center text-xs text-slate-500">{team.points_against}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BracketProgressBar({ schedule }) {
  const bracketMatches = schedule.filter(m => m.stage);
  const stages = ['QF', 'WB_SF', 'CB_SF', 'SF', 'F', '3RD', '5TH', '7TH', 'CHAMP'];
  const stageGroups = [];
  const stageOrder = [];

  for (const s of stages) {
    const matches = bracketMatches.filter(m => m.stage === s);
    if (matches.length) {
      stageOrder.push(s);
      stageGroups.push({ stage: s, matches, completed: matches.filter(m => m.winner).length, total: matches.length });
    }
  }

  if (!stageGroups.length) return null;

  const stageLabels = {
    QF: 'Quarters', WB_SF: 'Win SF', CB_SF: 'Con SF', SF: 'Semis',
    F: 'Finals', '3RD': '3rd', '5TH': '5th', '7TH': '7th', CHAMP: 'Champ'
  };

  return (
    <div className="flex items-center gap-1" data-testid="bracket-progress-bar">
      {stageGroups.map((sg, i) => {
        const pct = sg.total > 0 ? (sg.completed / sg.total) * 100 : 0;
        const isComplete = pct === 100;
        const isActive = pct > 0 && pct < 100;
        return (
          <React.Fragment key={sg.stage}>
            <div className="flex flex-col items-center gap-1">
              <div className={`h-2 rounded-full transition-all ${isComplete ? 'bg-green-500' : isActive ? 'bg-amber-400' : 'bg-slate-200'}`}
                style={{ width: Math.max(40, 60) }} />
              <span className={`text-[10px] font-medium ${isComplete ? 'text-green-600' : isActive ? 'text-amber-600' : 'text-slate-400'}`}>
                {stageLabels[sg.stage] || sg.stage}
              </span>
              <span className="text-[9px] text-slate-400">{sg.completed}/{sg.total}</span>
            </div>
            {i < stageGroups.length - 1 && (
              <ArrowRight className={`w-3 h-3 flex-shrink-0 mt-[-14px] ${isComplete ? 'text-green-400' : 'text-slate-300'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function TournamentDashboard() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentEvent, fetchEvent, loading } = useEvent();
  const { user } = useAuth();

  useEffect(() => {
    if (eventId) fetchEvent(eventId);
  }, [eventId, fetchEvent]);

  const stats = useMemo(() => {
    if (!currentEvent?.schedule) return null;
    const schedule = currentEvent.schedule;
    const total = schedule.length;
    const completed = schedule.filter(m => m.winner).length;
    const upcoming = schedule.filter(m => !m.winner && m.team_a && m.team_b && !m.team_a.startsWith('W_') && !m.team_a.startsWith('L_'));
    const recentResults = schedule.filter(m => m.winner).sort((a, b) => {
      if (a.date !== b.date) return (b.date || '').localeCompare(a.date || '');
      return (b.time || '').localeCompare(a.time || '');
    }).slice(0, 5);
    const upcomingNext = upcoming.sort((a, b) => {
      if (a.date !== b.date) return (a.date || '').localeCompare(b.date || '');
      return (a.time || '').localeCompare(b.time || '');
    }).slice(0, 5);
    const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;
    const gameDays = [...new Set(schedule.map(m => m.date).filter(Boolean))].length;
    return { total, completed, remaining: total - completed, upcoming: upcomingNext, recentResults, progressPct, gameDays };
  }, [currentEvent]);

  if (loading || !currentEvent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
      </div>
    );
  }

  const event = currentEvent;
  const isBracketFormat = ['single_elimination', 'double_elimination', 'consolation'].includes(event.format);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tournament-dashboard">
      {/* Header */}
      <div className="bg-[#00247D] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="text-white/80 hover:text-white hover:bg-white/10">
                <Home className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Barlow Condensed' }} data-testid="dashboard-title">
                  {event.name}
                </h1>
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{event.start_date}</span>
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{event.teams?.length} teams</span>
                  <Badge className="bg-white/20 text-white text-xs border-0">{FORMAT_LABELS[event.format] || event.format}</Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10"
                onClick={() => navigate(`/schedule/${eventId}`)} data-testid="nav-schedule-btn">
                <Calendar className="w-4 h-4 mr-1" /> Schedule
              </Button>
              {isBracketFormat && (
                <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => navigate(`/bracket/${eventId}`)} data-testid="nav-bracket-btn">
                  <GitBranch className="w-4 h-4 mr-1" /> Bracket
                </Button>
              )}
              <Button size="sm" className="bg-amber-400 text-[#00247D] hover:bg-amber-300 font-bold"
                onClick={() => navigate(`/export/${eventId}`)} data-testid="nav-export-btn">
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Progress + Stats Row */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Progress Ring */}
          <Card className="border-slate-200 lg:col-span-1" data-testid="progress-card">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <div className="relative">
                <ProgressRing percent={stats?.progressPct || 0} size={90} strokeWidth={7} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>{stats?.progressPct || 0}%</span>
                </div>
              </div>
              <span className="text-xs text-slate-500 mt-2">Tournament Progress</span>
            </CardContent>
          </Card>

          {/* Stat Cards */}
          <StatCard icon={Target} label="Total Matches" value={stats?.total || 0} testId="stat-total" />
          <StatCard icon={CheckCircle2} label="Completed" value={stats?.completed || 0} color="text-green-600" testId="stat-completed" />
          <StatCard icon={Clock} label="Remaining" value={stats?.remaining || 0} color="text-amber-600" testId="stat-remaining" />
          <StatCard icon={Calendar} label="Game Days" value={stats?.gameDays || 0} testId="stat-days" />
        </div>

        {/* Bracket Progress */}
        {isBracketFormat && event.schedule?.some(m => m.stage) && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <GitBranch className="w-4 h-4 text-[#00247D]" />
                BRACKET PROGRESS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BracketProgressBar schedule={event.schedule} />
            </CardContent>
          </Card>
        )}

        {/* Two Column: Upcoming + Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Matches */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Swords className="w-4 h-4 text-amber-500" />
                UPCOMING MATCHES
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="upcoming-matches">
              {stats?.upcoming?.length ? (
                stats.upcoming.map(m => <MatchRow key={m.id} match={m} isCompleted={false} />)
              ) : (
                <div className="py-6 text-center text-sm text-slate-400">
                  {stats?.remaining === 0 ? 'Tournament complete!' : 'No upcoming matches with confirmed teams'}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Results */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Medal className="w-4 h-4 text-green-500" />
                RECENT RESULTS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="recent-results">
              {stats?.recentResults?.length ? (
                stats.recentResults.map(m => <MatchRow key={m.id} match={m} isCompleted={true} />)
              ) : (
                <div className="py-6 text-center text-sm text-slate-400">
                  No results yet. Enter scores to see results here.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Standings */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <BarChart3 className="w-4 h-4 text-[#00247D]" />
              STANDINGS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CompactStandings standings={event.standings} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
